"use client";

import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Disclaimer } from "@/components/disclaimer";
import { TickerCombobox } from "@/components/portfolio/ticker-combobox";
import { formatCurrency, formatPercent } from "@/lib/format";
import type { SecurityRef } from "@/lib/data/types";

interface WhatIfApiResult {
  symbol: string;
  name: string;
  currency: string;
  requestedDate: string;
  actualStartDate: string;
  endDate: string;
  startPrice: number;
  endPrice: number;
  shares: number;
  priceChangePct: number;
  annualizedReturnPct: number | undefined;
  dividendPerShareTotal: number;
  dividendIncome: number;
  valueToday: number;
  totalReturnPct: number;
}

function StatTile({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-hairline bg-card p-3 text-center">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 font-tabular text-lg font-semibold text-ink">{value}</p>
    </div>
  );
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function fiveYearsAgoIso(): string {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 5);
  return d.toISOString().slice(0, 10);
}

/**
 * Oeffentlicher "Was wäre, wenn ich vor X Jahren investiert hätte"-Rechner (backlog.md ->
 * Aktienfinder-Benchmark) - Aktie, Startdatum und Betrag eingeben, echte Kurs-/
 * Dividendenhistorie via /api/what-if-invested. Rein retrospektiv-deskriptiv, keine
 * Prognose/Kaufempfehlung.
 */
export function WhatIfInvestedCalculator() {
  const [ticker, setTicker] = useState<SecurityRef | null>(null);
  const [date, setDate] = useState(fiveYearsAgoIso());
  const [amount, setAmount] = useState("10000");
  const [result, setResult] = useState<WhatIfApiResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit() {
    if (!ticker) {
      setError("Bitte zuerst eine Aktie auswählen.");
      return;
    }
    const parsedAmount = Number(amount.replace(",", "."));
    if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
      setError("Bitte einen gültigen Betrag eingeben.");
      return;
    }
    setIsLoading(true);
    setError(null);
    setResult(null);
    try {
      const res = await fetch("/api/what-if-invested", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          symbol: ticker.symbol,
          name: ticker.name,
          currency: ticker.currency,
          date,
          amount: parsedAmount,
        }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { error?: string } | null;
        setError(body?.error ?? "Berechnung aktuell nicht möglich.");
        return;
      }
      setResult((await res.json()) as WhatIfApiResult);
    } catch {
      setError("Daten aktuell nicht abrufbar.");
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="flex flex-col gap-4 rounded-lg border border-hairline p-4">
      <div>
        <span className="font-serif text-lg font-semibold text-ink">
          „Was wäre, wenn ich investiert hätte?“
        </span>
        <p className="mt-1 text-xs text-ink-secondary">
          Aktie, Startdatum und Betrag eingeben - zeigt die tatsächliche Wertentwicklung bis
          heute anhand echter historischer Kurs- und Dividendendaten. Rein retrospektiv, keine
          Prognose.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <label className="flex flex-col gap-1 text-xs text-muted-foreground sm:col-span-1">
          Aktie
          <TickerCombobox onSelect={setTicker} placeholder="Name, ISIN oder WKN …" />
        </label>
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          Investiert am
          <Input
            type="date"
            value={date}
            max={todayIso()}
            onChange={(e) => setDate(e.target.value)}
            className="bg-card"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          Betrag {ticker?.currency ? `(${ticker.currency})` : ""}
          <Input
            type="number"
            min={0}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="bg-card"
          />
        </label>
      </div>

      <Button type="button" onClick={handleSubmit} disabled={isLoading || !ticker} className="w-fit">
        {isLoading ? "Berechnet…" : "Berechnen"}
      </Button>

      {error && <p className="text-xs text-muted-foreground">{error}</p>}

      {result && (
        <div className="flex flex-col gap-3 rounded-lg border border-primary/20 bg-primary/[0.03] p-4">
          {result.actualStartDate !== result.requestedDate && (
            <p className="text-xs text-muted-foreground">
              Für {result.requestedDate} liegen keine Kursdaten vor - Berechnung ab dem
              nächstgelegenen verfügbaren Datum ({result.actualStartDate}).
            </p>
          )}
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <span className="text-xs font-medium text-ink-secondary">
              Aus {formatCurrency(Number(amount.replace(",", ".")), result.currency)} wäre heute geworden
            </span>
            <span className="font-tabular text-2xl font-semibold text-ink">
              {formatCurrency(result.valueToday, result.currency)}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatTile label="Gesamtrendite" value={formatPercent(result.totalReturnPct)} />
            <StatTile
              label="Rendite p. a."
              value={result.annualizedReturnPct !== undefined ? formatPercent(result.annualizedReturnPct) : "–"}
            />
            <StatTile label="Kursentwicklung" value={formatPercent(result.priceChangePct)} />
            <StatTile label="Dividenden erhalten" value={formatCurrency(result.dividendIncome, result.currency)} />
          </div>
        </div>
      )}

      <p className="text-[0.65rem] text-muted-foreground">
        Rückblickende Berechnung auf Basis echter historischer Kurse, ohne Wiederanlage/
        Verzinsung erhaltener Dividenden, ohne Steuern/Gebühren. Keine Aussage über die
        zukünftige Entwicklung, keine Kauf-/Verkaufsempfehlung.
      </p>
      <Disclaimer />
    </div>
  );
}
